// A tab loaded from an older deploy holds references to hashed JS chunks (lazy
// routes) that no longer exist once Cloudflare serves a newer build. Requesting
// one 404s and — because the SPA fallback returns index.html — the browser blocks
// it as a "disallowed MIME type (text/html)". When that happens we reload once to
// pull the fresh index + chunks.
//
// Shares its loop-guard key with the boot guard in index.html, and is guarded via
// sessionStorage so a genuinely broken deploy can't put the tab into a reload loop.

const RELOAD_KEY = 'appReloadAt'
const RELOAD_COOLDOWN_MS = 10_000

export function isChunkLoadError(error: unknown): boolean {
    const message = (error instanceof Error ? error.message : String(error ?? '')) || ''
    return /dynamically imported module|module script failed|failed to fetch dynamically imported|error loading dynamically imported|importing a module script failed|disallowed mime type/i.test(message)
}

// Force the browser to fetch a fresh index.html — and therefore the current asset
// hashes — instead of a stale, HTTP-cached shell. A plain location.reload() can be
// served straight from cache; navigating to a URL with a changed query string is a
// guaranteed cache miss. The path and fragment are kept, and location.replace()
// keeps the bounce out of the back/forward history.
export function reloadFresh(): void {
    const url = new URL(window.location.href)
    url.searchParams.set('_', Date.now().toString())
    window.location.replace(url.toString())
}

export function reloadOnceForChunkError(error: unknown): void {
    if (!isChunkLoadError(error)) {
        return
    }

    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
    const now = Date.now()

    if (now - last < RELOAD_COOLDOWN_MS) {
        return
    }

    sessionStorage.setItem(RELOAD_KEY, String(now))
    reloadFresh()
}
